import { BackgroundRecordsProvider } from '@tallo/applications';

import { AttestationResponseDto } from './send-application-request.interface';

/**
 * The send step's answer when the company is not set up at TransUnion yet and the person opening it has
 * no mailing address on file: they set the company up, so they are asked for one.
 */
export const transUnionAddressRequiredErrorCode = 'TRANS_UNION_ADDRESS_REQUIRED';

/**
 * The send's answer when a confirmation it relied on lapsed between opening the modal and sending: the modal
 * refetches the requirements and asks again.
 */
export const sendConfirmationRequiredErrorCode = 'SEND_CONFIRMATION_REQUIRED';

/** Why the attestations are asked: never confirmed for the property, or TransUnion added a question since. */
export type AttestationConfirmationReason = 'never-confirmed' | 'requirements-changed';

/** Why the caller's latest permissible-purpose certification for the property no longer covers a send. */
export type RecertificationReason = 'certification-expired' | 'wording-changed';

/**
 * Whether the property's stored attestations cover a send made now: confirmed by any seat of the company, and
 * answering everything TransUnion asks for the property today. They never expire.
 */
export interface AttestationCoverageDto {
  /** `covered`: the stored answers are replayed, nothing to ask; `confirmation-required`: ask again, for `reason`. */
  outcome: 'nothing-required' | 'covered' | 'confirmation-required';
  confirmedAt?: string;
  confirmedBy?: string;
  reason?: AttestationConfirmationReason;
  /** The answers last confirmed: what is replayed, or what to prefill when asking again. */
  previousAnswers?: AttestationResponseDto[];
}

/** The caller's own permissible-purpose certification for the property that still covers a send. */
export interface PermissiblePurposeCertificationStatusDto {
  certifiedAt: string;
  expiresAt: string;
}

/**
 * Everything the send modal collects for a property, and what is already on file so it asks only for the rest:
 * TransUnion's attestations and terms, and under RentPrep its terms and the permissible-purpose certification.
 */
export interface PropertyAttestationsDto {
  attestationGroupId: number;
  attestations: AttestationDto[];
  /** Who supplies the criminal + eviction reports for an application sent now. */
  backgroundRecordsProvider: BackgroundRecordsProvider;
  attestationCoverage: AttestationCoverageDto;
  /** The caller already agreed to TransUnion's Terms of Use (asked once per person), so the modal does not ask again. */
  transUnionTermsAccepted: boolean;
  /** The caller already accepted RentPrep's terms (asked once per person), so the modal does not ask again. */
  rentPrepTermsAccepted: boolean;
  /**
   * The permissible-purpose certification for this property, exactly as the backend records it when the owner
   * ticks it; null while TransUnion screens everything.
   */
  permissiblePurposeCertificationText: string | null;
  /** The caller's own certification that still covers a send; null when there is none, or while TransUnion screens everything. */
  permissiblePurposeCertification: PermissiblePurposeCertificationStatusDto | null;
  /** Why the certification is asked again; null when it is not asked, or asked for the first time. */
  permissiblePurposeCertificationReason: RecertificationReason | null;
}

export interface AttestationDto {
  attestationId: number;
  attestationTypeId: number;
  name: string;
  legalText: string;
  affirmativeRequired: boolean;
  additionalInformation: string;
}
